import { UseFormRegister, UseFormSetValue, UseFormWatch } from "react-hook-form";
import { Link } from "react-router-dom";
import { BanknotesIcon, CreditCardIcon, ShoppingBagIcon, TruckIcon } from "@heroicons/react/24/outline";

import { useAuth } from "../../contexts/AuthContext";

import { IAccount } from "../../models/account/IAccount";
import { IShippingPolicy } from "../../models/ShippingPolicy";
import { ITillConfig } from "../../models/till/ITillConfig";
import { ITillQuote } from "../../models/till/ITillQuote";
import classNames from "../../utils/classNames";
import PriceFormat from "../../utils/priceFormat";
import { isCashEnabled } from "../../utils/till/tenderValidation";
import { SearchAccount } from "../accounts/SearchAccount";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import DiscountTypeToggleInput from "../shared/Inputs/DiscountTypeToggleInput";
import useStockPermissions, { STOCK_DISCOUNT_LOCKED_MESSAGE } from "../../data/useStockPermissions";
import SelectInput from "../shared/Inputs/SelectInput";
import { TextInput } from "../shared/Inputs/TextInput";

export interface TillFormValues {
  discountType: string;
  discountValue: string;
  shippingPolicyId: string;
  notes: string;
  /** "Collected" or "Delivery". */
  fulfilment: string;
  line1: string;
  line2: string;
  town: string;
  county: string;
  postcode: string;
}

interface TillTotalsPaneProps {
  quote: ITillQuote | undefined;
  isQuoteLoading: boolean;
  quoteError?: string | null;
  config: ITillConfig | undefined;
  register: UseFormRegister<TillFormValues>;
  setValue: UseFormSetValue<TillFormValues>;
  watch: UseFormWatch<TillFormValues>;
  selectedAccount: IAccount | null;
  onAccountSelect: (account: IAccount) => void;
  shippingPolicies: IShippingPolicy[];
  canTender: boolean;
  onCash: () => void;
  onCard: () => void;
  onOther: (method: "BankTransfer") => void;
  disabled?: boolean;
}

/** Customer, discount, tax mode, server totals and the tender buttons. */
export default function TillTotalsPane({
  quote, isQuoteLoading, quoteError, config, register, setValue, watch, selectedAccount, onAccountSelect,
  shippingPolicies, canTender, onCash, onCard, onOther, disabled,
}: TillTotalsPaneProps) {
  const { restricted } = useStockPermissions();
  const { claims } = useAuth();
  const isAdmin = claims?.isAdmin === true;
  const policyOptions = shippingPolicies.map((p) => ({ id: p.id, name: `${p.name} (${PriceFormat(p.price)})` }));
  const cashEnabled = isCashEnabled(config);
  const fulfilment = watch("fulfilment");

  const row = (label: string, value: number | undefined, emphasis = false, negative = false) => (
    <div className={classNames("flex items-baseline justify-between", emphasis ? "text-2xl font-semibold text-gray-900" : "text-sm text-gray-700")}>
      <span>{label}</span>
      <span className={classNames("tabular-nums", negative ? "text-emerald-700" : "")}>{value == null ? "—" : `${negative ? "-" : ""}${PriceFormat(value)}`}</span>
    </div>
  );

  return (
    <section className="flex h-full flex-col min-h-0 bg-white">
      <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4">
        <SearchAccount label="Customer" onAccountSelect={onAccountSelect} selectedAccount={selectedAccount} disabled={disabled} />

        <div>
          <p className="block text-sm font-medium text-gray-700 mb-1">Fulfilment</p>
          <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Fulfilment">
            {([
              { value: "Collected", label: "Collecting now", icon: ShoppingBagIcon },
              { value: "Delivery", label: "Deliver", icon: TruckIcon },
            ] as const).map((opt) => (
              <button
                key={opt.value}
                type="button"
                role="radio"
                aria-checked={fulfilment === opt.value}
                disabled={disabled}
                onClick={() => setValue("fulfilment", opt.value)}
                className={classNames(
                  "flex h-12 items-center justify-center gap-2 rounded-md border text-sm font-medium",
                  fulfilment === opt.value ? "border-primary-500 bg-primary-50 text-primary-800 ring-1 ring-primary-300" : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50",
                  "disabled:opacity-50"
                )}
              >
                <opt.icon className="h-5 w-5" />
                {opt.label}
              </button>
            ))}
          </div>
          
        </div>

        {fulfilment === "Delivery" ? (
          <>
            <SelectInput
              label="Delivery option"
              register={register}
              registerName="shippingPolicyId"
              options={policyOptions}
              setValue={setValue}
              watch={watch}
              disabled={disabled}
              placeholder="Choose a delivery option"
            />
            <div className="rounded-md border border-gray-200 p-3 space-y-2">
              <p className="text-sm font-medium text-gray-700">Deliver to</p>
              <TextInput label="Address line 1" register={register} registerName="line1" disabled={disabled} required />
              <TextInput label="Address line 2" register={register} registerName="line2" disabled={disabled} />
              <div className="grid grid-cols-2 gap-2">
                <TextInput label="Town" register={register} registerName="town" disabled={disabled} />
                <TextInput label="County" register={register} registerName="county" disabled={disabled} />
              </div>
              <TextInput label="Postcode" register={register} registerName="postcode" disabled={disabled} required />
            </div>
          </>
        ) : null}

        <div className="rounded-md border border-gray-200 p-3">
          <p className="text-sm font-medium text-gray-700 mb-2">Discount</p>
          {/* Stock:AdminOnlyChanges - discounts are admin-only, matching the API */}
          {restricted ? (
            <p className="text-xs text-gray-500">{STOCK_DISCOUNT_LOCKED_MESSAGE}</p>
          ) : (
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <TextInput label="" register={register} registerName="discountValue" type="number" step="0.01" customClasses="h-[42px]" disabled={disabled} />
              </div>
              <DiscountTypeToggleInput label="" classes="shrink-0" register={register} registerName="discountType" setValue={setValue} watch={watch} />
            </div>
          )}
        </div>

        <TextInput label="Notes for the invoice" register={register} registerName="notes" disabled={disabled} />
      </div>

      <div className="shrink-0 border-t border-gray-200 p-4 space-y-1 bg-gray-50">
        {quoteError ? <p className="mb-2 rounded-md bg-amber-50 p-2 text-sm text-amber-800">{quoteError}</p> : null}
        {row("Goods (ex VAT)", quote?.subTotal)}
        {row("VAT on standard-rated items", quote?.tax)}
        {quote && quote.discount > 0 ? row("Discount", quote.discount, false, true) : null}
        {quote && quote.shipping > 0 ? row("Delivery", quote.shipping) : null}
        <div className="pt-2 border-t border-gray-200 mt-2">
          {row("Total", quote?.total, true)}
          {isQuoteLoading ? <p className="text-xs text-gray-400">Updating…</p> : null}
        </div>

        <div className={classNames("grid gap-3 pt-3", cashEnabled ? "grid-cols-2" : "grid-cols-1")}>
          {cashEnabled ? (
            <ButtonInput label="" size="lg" classes="h-16 !bg-emerald-600 hover:!bg-emerald-700 disabled:!bg-gray-400 !text-lg" onClick={onCash} disabled={disabled || !canTender}>
              <span className="inline-flex items-center gap-2 text-lg text-white"><BanknotesIcon className="h-6 w-6" /> Cash</span>
            </ButtonInput>
          ) : null}
          <ButtonInput label="" size="lg" classes="h-16 !bg-purple-600 hover:!bg-purple-700 disabled:!bg-gray-400 !text-lg" onClick={onCard} disabled={disabled || !canTender || !config?.stripeEnabled}>
            <span className="inline-flex items-center gap-2 text-lg text-white"><CreditCardIcon className="h-6 w-6" /> Card</span>
          </ButtonInput>
        </div>
        {!config?.stripeEnabled && config ? (
          <p className="text-xs text-gray-500">
            {config.stripeMessage ?? "Card payments are not set up."}
            {isAdmin ? <> <Link to="/admin/till" className="text-primary-700 hover:underline">Set up card payments</Link></> : null}
          </p>
        ) : null}
        {!cashEnabled ? <p className="text-xs text-gray-500">Cash payments are turned off for this till.</p> : null}

        <div className="flex justify-center pt-1">
          <button type="button" onClick={() => onOther("BankTransfer")} disabled={disabled || !canTender} className="text-sm text-primary-700 hover:underline disabled:text-gray-400">Bank transfer</button>
        </div>
      </div>
    </section>
  );
}
